import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import AddIcon from "@atlaskit/icon/core/add";
import MinusIcon from "@atlaskit/icon/core/minus";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import {
    SideNavCollection,
    SideNavCollectionProps
} from "@/libraries/design/components/side-nav/src/side-nav-collection/src/side-nav-collection";
import {
    SideNavLink,
    SideNavLinkProps
} from "@/libraries/design/components/side-nav/src/side-nav-link/src/side-nav-link";
import { wacToken } from "@/libraries/design/foundations";

export interface SideNavProps {
    title: string;
    sideNavItems: (SideNavCollectionProps | SideNavLinkProps)[];
}

const MobileExpandableNav = ({
    title,
    children
}: {
    title: string;
    children: React.ReactNode;
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [buttonBottom, setButtonBottom] = useState(0);
    const navRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    // Calculate button position for nav positioning
    useEffect(() => {
        const updateButtonPosition = () => {
            if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setButtonBottom(rect.bottom);
            }
        };

        updateButtonPosition();
        window.addEventListener("resize", updateButtonPosition);
        window.addEventListener("scroll", updateButtonPosition);

        return () => {
            window.removeEventListener("resize", updateButtonPosition);
            window.removeEventListener("scroll", updateButtonPosition);
        };
    }, []);

    // Prevent body scroll when nav is open
    useEffect(() => {
        if (isExpanded) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isExpanded]);

    // Handle click outside to close nav
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isExpanded &&
                navRef.current &&
                !navRef.current.contains(event.target as Node)
            ) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);

    return (
        <div ref={navRef} css={css({ position: "relative" })}>
            <button
                type="button"
                ref={buttonRef}
                onClick={handleToggle}
                css={mobileExpandableButtonCss}
                aria-expanded={isExpanded}
                aria-controls="mobile-side-nav-content"
            >
                <span css={mobileIconCss}>
                    {isExpanded ? <MinusIcon label="" /> : <AddIcon label="" />}
                </span>
                <span className="eyebrow-md">{title}</span>
            </button>
            <div
                ref={contentRef}
                id="mobile-side-nav-content"
                css={[
                    mobileNavContentCss,
                    isExpanded
                        ? css({
                              position: "fixed",
                              insetBlockStart: `${buttonBottom}px`,
                              insetInline: 0,
                              insetBlockEnd: 0,
                              width: "100vw",
                              height: `calc(100vh - ${buttonBottom}px)`,
                              maxHeight: `calc(100vh - ${buttonBottom}px)`,
                              opacity: 1,
                              zIndex: 10,
                              overflow: "hidden",
                              backgroundColor: wacToken("color.neutral.light0"),
                              paddingBlockStart: token("space.250"),
                              paddingBlockEnd: "60px",
                              paddingInline: "60px",
                              [media.below.sm]: {
                                  paddingInline: token("space.400")
                              }
                          })
                        : mobileNavContentCollapsedCss
                ]}
            >
                <div css={mobileInnerContentCss}>{children}</div>
            </div>
        </div>
    );
};

export const NavList = ({
    sideNavItems
}: {
    sideNavItems: (SideNavCollectionProps | SideNavLinkProps)[];
}) => {
    return (
        <ul className="sidenav-menu" css={listCss}>
            {sideNavItems.map((sideNavItem) => {
                if (sideNavItem.type === "collection") {
                    return (
                        <SideNavCollection
                            key={sideNavItem.collectionName}
                            type="collection"
                            collectionName={sideNavItem.collectionName}
                            sideNavItems={sideNavItem.sideNavItems}
                            depth={0}
                        />
                    );
                }
                if (sideNavItem.type === "link") {
                    return (
                        <SideNavLink
                            key={sideNavItem.text}
                            dataEventProps={sideNavItem.dataEventProps}
                            type="link"
                            text={sideNavItem.text}
                            href={sideNavItem.href}
                            anchors={sideNavItem.anchors}
                            depth={0}
                        />
                    );
                }
                return null;
            })}
        </ul>
    );
};

export const SideNav = ({ title, sideNavItems }: SideNavProps) => {
    const intl = useIntl();

    return (
        <div>
            <nav
                id="side-nav-desktop"
                aria-label={intl.formatMessage(
                    {
                        id: "hub.sidenav.desktop.label",
                        defaultMessage: `{hub_topics} desktop navigation`
                    },
                    { hub_topics: title }
                )}
                css={[desktopCss, containerCss]}
            >
                <div className="eyebrow-md" css={desktopTitleCss}>
                    {title}
                </div>
                <NavList sideNavItems={sideNavItems} />
            </nav>
            <nav
                id="side-nav-tablet-mobile"
                aria-label={intl.formatMessage(
                    {
                        id: "hub.sidenav.mobile.label",
                        defaultMessage: `{hub_topics} mobile navigation`
                    },
                    { hub_topics: title }
                )}
                css={[tabletMobileCss]}
            >
                <MobileExpandableNav title={title}>
                    <NavList sideNavItems={sideNavItems} />
                </MobileExpandableNav>
            </nav>
        </div>
    );
};

const desktopCss = css({
    [media.below.md]: {
        display: "none"
    }
});

const tabletMobileCss = css({
    [media.above.md]: {
        display: "none"
    }
});

const listCss = css({
    "listStyle": "none",
    "margin": token("space.0"),
    "padding": token("space.0"),
    "overflow": "auto",

    [media.above.md]: {
        maxHeight: "calc(100vh - 240px)"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> li + li": {
        borderBlockStart: `1px solid ${wacToken("color.neutral.light300")}`
    }
});

const containerCss = css({
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    borderRadius: wacToken("corner.radius.card"),
    padding: token("space.250"),
    paddingBlockEnd: "60px",
    height: "fit-content"
});

const desktopTitleCss = css({
    display: "flex",
    alignItems: "center",
    minHeight: "60px",
    padding: `${token("space.200")} 28px`
});

const mobileIconCss = css({
    height: token("space.300"),
    width: token("space.300"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: wacToken("color.primary.blue")
});

const mobileExpandableButtonCss = css({
    "width": "100%",
    "height": "60px",
    "backgroundColor": wacToken("color.neutral.light200"),
    "border": "none",
    "padding": `${token("space.200")} 60px`,
    "display": "flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "cursor": "pointer",
    "transition": "background-color 0.2s ease",
    "position": "relative",
    "zIndex": 11,
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light300")
    },
    [media.below.sm]: {
        paddingInline: token("space.400")
    }
});

const mobileNavContentCss = css({
    backgroundColor: wacToken("color.neutral.light0"),
    width: "100%",
    paddingInline: "60px",
    transition:
        "max-height 0.3s ease, opacity 0.3s ease, inset-block-start 0.3s ease",
    overflow: "auto",
    [media.below.sm]: {
        paddingInline: token("space.400")
    }
});

const mobileNavContentCollapsedCss = css({
    position: "fixed",
    maxHeight: 0,
    opacity: 0,
    pointerEvents: "none",
    insetBlockStart: 0,
    insetInline: 0,
    width: "100vw"
});

const mobileInnerContentCss = css({
    height: "100%",
    overflow: "auto"
});
